import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Save, Send, RefreshCw, Megaphone, FileText, Shield, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase F.6 iter136 — Platform Admin: Reference Docs editor + Broadcasts.
 * Visible only to is_platform_admin users. Renders at /admin/comms.
 */
export default function AdminComms() {
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.is_platform_admin;

  if (!isPlatformAdmin) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6" data-testid="admin-comms-no-access">
        <div className="text-center">
          <Shield className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-semibold">Platform Admin Only</h2>
          <p className="text-sm text-muted-foreground">This page is reserved for the Business Genie owner account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="admin-comms-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Megaphone className="h-7 w-7" /> Owner Communications
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edit the reference documents seeded into every subscriber&apos;s portal and send platform-wide announcements.
        </p>
      </div>

      <Tabs defaultValue="docs" className="w-full">
        <TabsList>
          <TabsTrigger value="docs" data-testid="tab-reference-docs">
            <FileText className="h-4 w-4 mr-1" /> Reference Docs
          </TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="tab-broadcast">
            <Megaphone className="h-4 w-4 mr-1" /> Send Broadcast
          </TabsTrigger>
        </TabsList>

        <TabsContent value="docs" className="mt-4">
          <ReferenceDocsEditor />
        </TabsContent>
        <TabsContent value="broadcast" className="mt-4">
          <BroadcastComposer />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ReferenceDocsEditor = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // slug -> doc draft
  const [saving, setSaving] = useState({});
  const [pushing, setPushing] = useState({});
  const [previewSlug, setPreviewSlug] = useState(null); // which doc is being previewed

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reference-docs`);
      setDocs(r.data?.docs || []);
      // hydrate edit drafts
      const drafts = {};
      for (const d of r.data?.docs || []) drafts[d.slug] = { ...d };
      setEditing(drafts);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load reference docs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const onField = (slug, key, val) => {
    setEditing((prev) => ({ ...prev, [slug]: { ...prev[slug], [key]: val } }));
  };

  const save = async (slug) => {
    setSaving((s) => ({ ...s, [slug]: true }));
    try {
      const draft = editing[slug];
      const r = await axios.put(`${API}/admin/reference-docs/${slug}`, {
        title: draft.title,
        content_md: draft.content_md,
        description: draft.description,
        is_active: draft.is_active,
      });
      setDocs((rows) => rows.map((d) => (d.slug === slug ? r.data : d)));
      toast.success("Saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [slug]: false }));
    }
  };

  const pushToTenants = async (slug, overwrite) => {
    if (overwrite && !window.confirm(
      "OVERWRITE will replace this doc in EVERY subscriber tenant, including their customised edits. Continue?"
    )) return;
    setPushing((s) => ({ ...s, [slug]: true }));
    try {
      const r = await axios.post(`${API}/admin/reference-docs/push-to-tenants`, { slug, overwrite });
      toast.success(`Pushed: ${r.data.created} created, ${r.data.updated} updated, ${r.data.skipped} skipped of ${r.data.tenants_total} tenants`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Push failed");
    } finally {
      setPushing((s) => ({ ...s, [slug]: false }));
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!docs.length) return <p className="text-sm text-muted-foreground">No reference docs configured yet.</p>;

  return (
    <div className="space-y-6">
      {docs.map((d) => {
        const draft = editing[d.slug] || d;
        const dirty = JSON.stringify(draft) !== JSON.stringify(d);
        return (
          <Card key={d.slug} data-testid={`refdoc-card-${d.slug}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    {draft.title}
                    {!d.is_active && <Badge variant="outline">Inactive</Badge>}
                    {dirty && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Unsaved</Badge>}
                  </CardTitle>
                  <CardDescription>{d.description || ""}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!draft.is_active}
                    onCheckedChange={(v) => onField(d.slug, "is_active", v)}
                    data-testid={`refdoc-active-${d.slug}`}
                  />
                  <Label className="text-xs text-muted-foreground">Active</Label>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Title shown to subscribers</Label>
                <Input
                  value={draft.title || ""}
                  onChange={(e) => onField(d.slug, "title", e.target.value)}
                  data-testid={`refdoc-title-${d.slug}`}
                />
              </div>
              <div>
                <Label className="text-xs">Content (Markdown)</Label>
                <Textarea
                  rows={18}
                  value={draft.content_md || ""}
                  onChange={(e) => onField(d.slug, "content_md", e.target.value)}
                  className="font-mono text-xs"
                  data-testid={`refdoc-content-${d.slug}`}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {(draft.content_md || "").length.toLocaleString()} characters • last edited {d.updated_at ? new Date(d.updated_at).toLocaleString() : "—"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => save(d.slug)}
                  disabled={!dirty || saving[d.slug]}
                  data-testid={`refdoc-save-${d.slug}`}
                >
                  {saving[d.slug] && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" /> Save Template
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setPreviewSlug(d.slug)}
                  data-testid={`refdoc-preview-${d.slug}`}
                  title="See what a subscriber sees when they open this doc"
                >
                  <Eye className="h-4 w-4 mr-2" /> Preview as Subscriber
                </Button>
                <Button
                  variant="outline"
                  onClick={() => pushToTenants(d.slug, false)}
                  disabled={dirty || pushing[d.slug]}
                  data-testid={`refdoc-push-${d.slug}`}
                  title={dirty ? "Save your changes first" : "Add this doc to any tenant that doesn't have it yet"}
                >
                  {pushing[d.slug] && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Send className="h-4 w-4 mr-2" /> Push to Missing Tenants
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => pushToTenants(d.slug, true)}
                  disabled={dirty || pushing[d.slug]}
                  data-testid={`refdoc-overwrite-${d.slug}`}
                  title="Replace this doc in EVERY tenant (overwrites subscriber edits)"
                >
                  <RefreshCw className="h-4 w-4 mr-2" /> Overwrite All Tenants
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Preview-as-Subscriber dialog: renders the (possibly unsaved) Markdown
          exactly the way a tenant subscriber would see it in their portal. */}
      <Dialog open={!!previewSlug} onOpenChange={(o) => !o && setPreviewSlug(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="refdoc-preview-dialog">
          {previewSlug && (() => {
            const d = editing[previewSlug] || docs.find((x) => x.slug === previewSlug) || {};
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" /> Preview · {d.title}
                  </DialogTitle>
                  <DialogDescription>
                    This is exactly what a subscriber sees in their portal. Use the editor behind to make changes — the preview updates instantly.
                  </DialogDescription>
                </DialogHeader>
                <article className="prose prose-sm dark:prose-invert max-w-none mt-4" data-testid={`refdoc-preview-content-${previewSlug}`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{d.content_md || "_(empty)_"}</ReactMarkdown>
                </article>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BroadcastComposer = () => {
  const [form, setForm] = useState({
    title: "",
    message: "",
    notification_type: "info",
    action_url: "",
    send_email: false,
    audience: "owners",
    test_only_to_self: false,
  });
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);

  const fetchRecent = async () => {
    try {
      const r = await axios.get(`${API}/admin/broadcasts/recent?limit=10`);
      setRecent(r.data?.broadcasts || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchRecent(); }, []);

  const send = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Title and message required");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/broadcasts`, form);
      toast.success(
        `Delivered to ${r.data.delivered_in_app} subscribers` +
        (form.send_email ? `, ${r.data.queued_emails} emails queued` : "")
      );
      if (!form.test_only_to_self) {
        setForm({ ...form, title: "", message: "", action_url: "" });
      }
      fetchRecent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2" data-testid="broadcast-composer-card">
        <CardHeader>
          <CardTitle>Compose Broadcast</CardTitle>
          <CardDescription>
            This goes to every subscriber admin&apos;s in-app notification bell. Optionally also fires an email via Resend.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. New feature: Magic-link doc signing is live"
              maxLength={200}
              data-testid="broadcast-title"
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Hey teams — quick update..."
              maxLength={5000}
              data-testid="broadcast-message"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {form.message.length}/5000
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <select
                className="w-full mt-1 border rounded h-9 px-2 bg-background"
                value={form.notification_type}
                onChange={(e) => setForm({ ...form, notification_type: e.target.value })}
                data-testid="broadcast-type"
              >
                <option value="info">Info (blue)</option>
                <option value="success">Success (green)</option>
                <option value="warning">Warning (amber)</option>
                <option value="alert">Alert (red)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Audience</Label>
              <select
                className="w-full mt-1 border rounded h-9 px-2 bg-background"
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                data-testid="broadcast-audience"
              >
                <option value="owners">Tenant Owners (office_manager)</option>
                <option value="all_admins">All Admin Users (office_manager + field_supervisor)</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Action URL (optional — opens when notification clicked)</Label>
            <Input
              value={form.action_url}
              onChange={(e) => setForm({ ...form, action_url: e.target.value })}
              placeholder="/dashboard or https://docs.yourbusinessgenie.com/release-notes"
              data-testid="broadcast-action-url"
            />
          </div>
          <div className="flex flex-wrap gap-4 items-center pt-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.send_email}
                onCheckedChange={(v) => setForm({ ...form, send_email: v })}
                data-testid="broadcast-send-email"
              />
              <Label className="text-sm">Also send email</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.test_only_to_self}
                onCheckedChange={(v) => setForm({ ...form, test_only_to_self: v })}
                data-testid="broadcast-test-only"
              />
              <Label className="text-sm">Test mode — send only to me</Label>
            </div>
          </div>
          <Button onClick={send} disabled={sending} className="w-full" data-testid="broadcast-send">
            {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Send className="h-4 w-4 mr-2" />
            {form.test_only_to_self ? "Send Test To Myself" : "Send to All Subscribers"}
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="broadcast-recent-card">
        <CardHeader>
          <CardTitle>Recent Broadcasts</CardTitle>
          <CardDescription>Last 10 platform announcements you sent.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {recent.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="broadcast-recent-empty">
              You haven&apos;t sent any broadcasts yet.
            </p>
          )}
          {recent.map((b, idx) => (
            <div key={idx} className="border rounded p-3 text-xs space-y-1" data-testid={`broadcast-recent-${idx}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm">{b.title}</p>
                <Badge variant="outline">{b.recipient_count}</Badge>
              </div>
              <p className="text-muted-foreground line-clamp-3 whitespace-pre-wrap">{b.message}</p>
              <p className="text-muted-foreground/80 text-[10px]">
                {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
